import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Brain,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  Feather,
  Moon,
  MoreHorizontal,
  PenLine,
  Play,
  Sparkles,
  Sun,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import useRetryFetch from "../../../hooks/useRetryFetch";
import { useResumeRunning } from "../../../hooks/useResumeRunning";
import { finishNow, type NowSessionDto } from "../../../api/now";
import type { RitualKey } from "../../../api/lifeOs";
import { stageMmOpen } from "../../../lib/mmHandoff";
import { stageWorkspaceItem } from "../../../lib/workspaceHandoff";
import { dueDateText, dueLabelOf } from "../../../lib/dueDates";
import type { ForNowCandidate, ForNowChoice, ForNowLength, ForNowText } from "../../../lib/forNow";
import NowTake from "../../now/NowTake";
import NowEveningCard from "../../now/NowEveningCard";
import { RitualSession } from "../cockpit/Rituals";
import { Button } from "../../ui/button";

// "For now" — exactly one suggestion (spec 2026-11-04 §2.4, §5). Everything
// else waiting folds into "N more waiting". The evening step check answers
// inline; every other kind has one secondary button.

const RITUAL_ICON = { morning: Sun, evening: Moon, weekly: Sparkles } as const;

const iconOf = (c: ForNowCandidate): React.ElementType => {
  switch (c.kind) {
    case "resume":
      return PenLine;
    case "ritual":
      return RITUAL_ICON[c.ritual];
    case "evening_step":
      return CheckCircle2;
    case "activity":
      return Play;
    case "model":
      return Brain;
    case "overdue_step":
      return CalendarClock;
    default:
      return Feather;
  }
};

interface Props {
  choice: ForNowChoice;
  ready: boolean;
  onDismiss: (key: string) => void;
  onWriteFirst: () => void;
  now?: () => Date;
}

const ForNowCard = ({ choice, ready, onDismiss, onWriteFirst, now = () => new Date() }: Props) => {
  const { t, i18n } = useTranslation();
  const retryFetch = useRetryFetch();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const resumeRunning = useResumeRunning();
  const [take, setTake] = useState<NowSessionDto | null>(null);
  const [ritual, setRitual] = useState<RitualKey | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    if (take) heading.current?.focus();
  }, [take]);
  // A new suggestion starts clean: an earlier failure belonged to the old one.
  const firstKey = choice.first.key;
  useEffect(() => setFailed(false), [firstKey]);
  // After "Not now" the menu item is gone; land focus on the For now label.
  const label = useRef<HTMLParagraphElement>(null);
  const [focusTick, setFocusTick] = useState(0);
  useEffect(() => {
    if (focusTick) label.current?.focus();
  }, [focusTick]);
  const dismiss = (key: string) => {
    onDismiss(key);
    setFocusTick((n) => n + 1);
  };

  const dayText = (ts: number): string => {
    const label = dueLabelOf(ts, now().getTime());
    return label.kind === "today" ? t("todayHome.day.today") : dueDateText(label, i18n.language);
  };
  const show = (ref: ForNowText | null): string | null => {
    if (!ref) return null;
    if ("text" in ref) return ref.text;
    const params: Record<string, string | number> = { ...(ref.params ?? {}) };
    if (ref.dayTs != null) params.day = dayText(ref.dayTs);
    return Object.keys(params).length ? t(ref.key, params) : t(ref.key);
  };
  const lengthText = (l: ForNowLength | null): string | null =>
    !l ? null : l.kind === "minutes" ? t("todayHome.forNow.length.minutes", { count: l.count }) : t("todayHome.forNow.length.few");
  const thanks = () => toast.success(t("now.evening.thanks"));

  const run = async (c: ForNowCandidate) => {
    switch (c.kind) {
      case "resume": {
        setBusy(true);
        setFailed(false);
        try {
          const { session } = await finishNow(retryFetch, c.resume.sessionId, { tool: c.resume.route });
          setTake(session);
        } catch {
          setFailed(true);
        } finally {
          setBusy(false);
        }
        return;
      }
      case "ritual":
        setRitual(c.ritual);
        return;
      case "activity":
        await resumeRunning(c.process);
        return;
      case "model":
        stageMmOpen(c.objectId);
        navigate("/portal");
        return;
      case "overdue_step":
        stageWorkspaceItem("action_items", c.takeawayId);
        navigate("/portal");
        return;
      case "write_first":
        onWriteFirst();
        return;
      default:
        return; // evening_step answers inline
    }
  };

  if (!ready) {
    return <div data-testid="for-now-loading" aria-hidden className="h-14 rounded-xl bg-muted/40 motion-safe:animate-pulse" />;
  }

  if (take) {
    return (
      <section className="rounded-2xl border border-border bg-card p-4">
        <NowTake
          headingRef={heading}
          session={take}
          onClose={() => {
            setTake(null);
            qc.invalidateQueries({ queryKey: ["now-today"] });
          }}
        />
      </section>
    );
  }

  const first = choice.first;
  const Icon = iconOf(first);
  const why = show(first.why);
  const len = lengthText(first.length);

  // A quiet row under capture, not a second hero: no card, small icon, one text line of why and length.
  return (
    <div className="space-y-0.5">
      <section aria-labelledby="for-now-label" className="rounded-xl bg-muted/40 px-3 py-2 space-y-2">
        <div className="flex items-center gap-2.5">
          <Icon className="h-4 w-4 shrink-0 text-primary/70" aria-hidden />
          <div className="min-w-0 flex-1">
            <p dir="auto" className="text-sm text-foreground line-clamp-2">
              <span id="for-now-label" ref={label} tabIndex={-1} className="outline-none text-muted-foreground">
                {t("todayHome.forNow.label")}
              </span>
              <span aria-hidden className="text-muted-foreground"> · </span>
              <span>{show(first.title)}</span>
            </p>
            {(why || len) && (
              <p dir="auto" className="text-xs text-muted-foreground line-clamp-1">
                {why && <span>{why}</span>}
                {why && len && <span aria-hidden> · </span>}
                {len && <span>{len}</span>}
              </p>
            )}
          </div>
          {first.kind !== "evening_step" && (
            <Button
              variant="ghost"
              size="sm"
              className="min-h-11 shrink-0 rounded-full px-3 text-primary hover:text-primary"
              disabled={busy}
              onClick={() => run(first)}
            >
              {t(`todayHome.forNow.action.${first.action}`)}
            </Button>
          )}
          {first.kind !== "write_first" && <NotNowMenu onDismiss={() => dismiss(first.key)} />}
        </div>
        {first.kind === "evening_step" && <NowEveningCard now={now} embedded onSaved={thanks} />}
        {failed && <p role="alert" className="text-sm text-destructive">{t("now.error")}</p>}
      </section>

      {choice.more.length > 0 && (
        <div>
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls="for-now-more"
            onClick={() => setExpanded((e) => !e)}
            className="inline-flex min-h-11 items-center gap-1 px-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {/* The rows that actually expand (at most three), not every candidate left. */}
            {t("todayHome.forNow.more", { count: choice.more.length })}
            <ChevronRight className={cn("h-4 w-4 rtl:rotate-180 motion-safe:transition-transform", expanded && "rotate-90 rtl:rotate-90")} aria-hidden />
          </button>
          {expanded && (
            <ul id="for-now-more" className="space-y-1">
              {choice.more.map((c) => {
                const rowWhy = [show(c.why), lengthText(c.length)].filter(Boolean).join(" · ");
                return (
                  <li key={c.key} className="flex items-start gap-2 rounded-xl px-2">
                    {c.kind === "evening_step" ? (
                      <div className="min-w-0 flex-1 py-2 space-y-2">
                        <p dir="auto" className="text-sm text-foreground">{show(c.title)}</p>
                        {rowWhy && <p dir="auto" className="text-xs text-muted-foreground">{rowWhy}</p>}
                        <NowEveningCard now={now} embedded onSaved={thanks} />
                      </div>
                    ) : (
                      <button type="button" onClick={() => run(c)} className="min-h-11 min-w-0 flex-1 py-2 text-start">
                        <span dir="auto" className="block text-sm text-foreground">{show(c.title)}</span>
                        {rowWhy && <span dir="auto" className="block text-xs text-muted-foreground">{rowWhy}</span>}
                      </button>
                    )}
                    <NotNowMenu onDismiss={() => dismiss(c.key)} />
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {ritual && <RitualSession ritualKey={ritual} onClose={() => setRitual(null)} />}
    </div>
  );
};

/** One-item overflow: "Not now" hides the candidate until tomorrow. */
const NotNowMenu = ({ onDismiss }: { onDismiss: () => void }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="relative shrink-0" onKeyDown={(e) => { if (e.key === "Escape") setOpen(false); }}>
      <button
        type="button"
        aria-label={t("todayHome.forNow.options")}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground hover:bg-muted"
      >
        <MoreHorizontal className="h-4 w-4" aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute end-0 top-full z-10 mt-1 min-w-[9rem] rounded-xl border border-border bg-popover p-1 shadow-md">
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onDismiss();
            }}
            className="w-full min-h-11 rounded-lg px-3 text-start text-sm hover:bg-muted"
          >
            {t("todayHome.forNow.notNow")}
          </button>
        </div>
      )}
    </div>
  );
};

export default ForNowCard;
